import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ChannelType } from '../../types';
import {
  commonBackInfo,
  dealwithChannelToResult,
  getNoSelectAndSelectValue,
} from '../../utils';
import ChannelLabel, {
  ChannelIconType,
  channelInfo,
} from '../../../ChannelLabel';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import useDefaultTwoLevelFilter from '../../hooks/useDefaultTwoLevelFilter';
import {
  channelLocale,
  channelSplitFlag,
  channelText,
} from '../../constants/channel';
import { transChannel } from '@/pages/opinion/utils';
import { addFilterBuriedPoint } from '../../utils/point';
import './index.scss';
import { useOptionsMemo } from '../../hooks/common';
import { channelExceedLimitTips } from '../../../BubbleCommon';
import { Bubble } from 'tea-component';
import useIntersectionObserver from '@/pages/opinion/pages/Summary/hooks/useIntersectionObserver';
import useLanguage from '@op/hooks/useLanguage';
import { getLocale } from 'umi';

const MAX_CHANNEL_LIMIT = 2000;

export default function MultiChannelSelect({
  changeOnCloseWindow,
  onChange,
  defaultValue,
  defaultKey,
  filterOptions,
  filterValue,
  lang,
  firstExcludeOptions,
  oriDefaultFilter,
  pointKey,
  dealwithOptions,
  setConfirmDisable,
}: ChannelType & { setConfirmDisable?: (value: boolean) => void; }) {
  const { formatMessage } = useLanguage();

  const [channelOptions, length, valueMap]: any = useMemo(() => {
    if (!filterOptions) return [];

    const options: any = [];
    const valueMap: { [key: string]: string[] } = {};
    let length = 0;
    Object.keys(channelText || {}).forEach((newKey) => {
      const element = filterOptions[newKey] || [];
      if (element.length > 0) {
        valueMap[newKey] = [];
        const children = element.map((text: ChannelIconType) => {
          const realText = text.toLocaleLowerCase();
          if (valueMap[newKey].includes(realText)) {
            return null;
          }
          const { img, name } = channelInfo(text);
          const realName = transChannel(name);
          valueMap[newKey].push(realText);
          return {
            icon: img,
            text: realName,
            textCN: realName,
            value: `${newKey}${channelSplitFlag}${realText}`,
          };
        }).filter((item: any) => item !== null);
        length += children.length;
        options.push({
          text: channelText[newKey].en,
          textCN: channelText[newKey].zh,
          value: newKey,
          children,
        });
      }
    });
    return [options, length, valueMap];
  }, [filterOptions]);
  const ref = useRef(null);

  const { show } = useIntersectionObserver({
    target: ref.current,
    threshold: 1,
  });

  const dealwithAllValue = (options: any) => {
    const markets: Array<string> = [];
    const regions: Array<string> = [];
    options.map((item: { value: string; children: any[] }) => {
      regions.push(item.value);
      markets.push(...item.children.map((cItem) => cItem.value));
    });
    return {
      markets,
      regions,
      type: 'market',
    };
  };

  function dealwithDefaultValue(value: any, isFirst: boolean) {
    let channel: Array<string> = [];
    const {
      channel_comments,
      channel_news,
      channel_securitys,
      channel_social,
    } = value;
    [
      { key: 'social', value: channel_social },
      { key: 'comments', value: channel_comments },
      { key: 'news', value: channel_news },
      { key: 'security', value: channel_securitys },
    ].forEach(({ key, value }) => {
      const allValue = getNoSelectAndSelectValue({
        value,
        originalOptions: (valueMap[key] || []).map(
          (item: string) => `${key}${channelSplitFlag}${item}`,
        ),
        getRealValue: (item: string) =>
          item.includes(channelSplitFlag)
            ? item.toLocaleLowerCase()
            : `${key}${channelSplitFlag}${item.toLocaleLowerCase()}`,
      });
      channel.push(...allValue);
    });

    // 首次加载或所有渠道为全选状态时，应用 firstExcludeOptions 排除项
    // 全选状态判断：渠道 value 为空数组或 undefined，表示该渠道全选
    const allChannelsSelectAll =
      (!channel_comments ||
        (Array.isArray(channel_comments) && channel_comments.length === 0)) &&
      (!channel_news ||
        (Array.isArray(channel_news) && channel_news.length === 0)) &&
      (!channel_securitys ||
        (Array.isArray(channel_securitys) && channel_securitys.length === 0)) &&
      (!channel_social ||
        (Array.isArray(channel_social) && channel_social.length === 0));

    if ((isFirst || allChannelsSelectAll) && firstExcludeOptions) {
      channel = [...channel].filter(
        (item) => !firstExcludeOptions.includes(item),
      );
    }
    return {
      value: channel.length === 0 ? ['-99999'] : channel,
      type: 'market',
    };
  }

  const realChannelOptions = useOptionsMemo({
    options: channelOptions,
    dealwithOptions,
    key: {
      social: 'channel_social',
      comments: 'channel_comments',
      news: 'channel_news',
      security: 'channel_securitys',
    },
    deeps: [channelOptions],
  });

  const {
    setSelectValue,
    selectValue,
    onChange: onChannelChange,
    diffClassName,
  } = useDefaultTwoLevelFilter({
    originalOptions: realChannelOptions,
    defaultValue,
    filterValue,
    oriDefaultFilter,
    onlySecondLevelValue: true,
    dealwithDefaultValue,
    dealwithAllValue,
  });

  // 检查实际传给后端的数据量是否超出限制（全选不计入，news反选时用排除数）
  const isExceeded = useMemo(() => {
    const selectedValues = onChannelChange(selectValue).value;
    const selectedSet = new Set(selectedValues);
    let transmitCount = 0;
    (realChannelOptions || []).forEach((category: { value: string; children: { value: string; }[]; }) => {
      const children = category.children || [];
      const selectedInCategory = children.filter(
        (child: { value: string; }) => selectedSet.has(child.value),
      ).length;
      if (selectedInCategory === 0 || selectedInCategory === children.length) {
        return;
      }
      if (category.value === 'news' && selectedInCategory > children.length / 2) {
        transmitCount += children.length - selectedInCategory;
      } else {
        transmitCount += selectedInCategory;
      }
    });
    return transmitCount > MAX_CHANNEL_LIMIT;
  }, [selectValue, realChannelOptions]);

  const showError = isExceeded && show;

  useEffect(() => {
    setConfirmDisable?.(isExceeded);
  }, [isExceeded]);

  const showErrorInfo = useMemo(() => {
    if (!showError) return null;
    const locale = getLocale();
    const key = locale === 'zh-CN' ? 'zh' : 'en';
    return channelExceedLimitTips[key] || null;
  }, [showError, show]);

  // console.log(selectValue, '----------');

  function buttonReturn(value: any, buttonEle: any, returnValue: boolean) {
    if (returnValue) {
      return { data: value, buttonEle };
    }
    return buttonEle;
  }

  const getName = useCallback((value: any) => {
    value = value?.split(channelSplitFlag)?.[1] || value;
    const { name } = channelInfo(value);
    return [name, value];
  }, []);

  const buttonElement = useCallback(
    (selected = selectValue, returnValue = false): any => {
      const { value } = onChannelChange(selected);
      // 全选先返回
      if (value.length === length) {
        return buttonReturn(value, formatMessage({ id: 'All' }), returnValue);
      }
      // 未选先返回
      if (value.length === 0) {
        return buttonReturn(value, formatMessage({ id: 'None' }), returnValue);
      }
      // 超过2个就直接返数字
      if (value.length > 2) {
        return buttonReturn(value, value.length, returnValue);
      }

      return buttonReturn(
        value,
        <>
          {value.map((item, index) => {
            const [_name, channelName] = getName(item);
            return (
              <div className="dbo-select-channel-item" key={channelName}>
                <ChannelLabel
                  className="dbo-select-channel-icon"
                  channel_name={channelName}
                  iconStyle={{ marginRight: 0 }}
                  showText
                ></ChannelLabel>
                {index + 1 === value.length ? '' : ','}
              </div>
            );
          })}
        </>,
        returnValue,
      );
    },
    [selectValue],
  );

  const channelChange = (tags: string) => {
    setSelectValue(tags);
  };

  function getChannelData() {
    const { data, buttonEle } = buttonElement(selectValue, true);
    const isAll = ['All', '全部'].includes(buttonEle);
    const { deleteKey, result } = dealwithChannelToResult(
      data,
      isAll,
      valueMap,
    );
    const { channel_security, ...otherChannel } = result;

    const obj: any = {
      ...otherChannel,
      channel_securitys: channel_security,
    };

    return { obj, deleteKey, isAll, buttonEle };
  }

  function getCommonBackInfo() {
    const { obj, deleteKey, isAll, buttonEle } = getChannelData();

    return {
      data: commonBackInfo(
        obj,
        { [defaultKey]: buttonEle },
        { needChangeEntity: false, isAll },
      ),
      deleteKey,
    };
  }

  useEffect(() => {
    const { data, deleteKey } = getCommonBackInfo();
    onChange?.('channel', data, deleteKey);
  }, [selectValue, formatMessage]);

  useEffect(() => {
    const { data } = getCommonBackInfo();
    changeOnCloseWindow?.(data.value);
  }, [selectValue]);

  const buttonElementMemo = useMemo(() => {
    const key = lang === 'zh-CN' ? 'cn' : 'en';
    const ele = buttonElement();
    return (
      <div>
        <span className="select-button-text">
          {channelLocale[key].market_title}
        </span>
        {ele}
      </div>
    );
  }, [buttonElement, lang]);

  return (
    <div ref={ref} className={`dbo-select-multi-channel-wrapper ${diffClassName} ${showError ? 'dbo-select-multi-channel-wrapper-error' : ''}`}>
      <Bubble
        visible={show}
        error={true} 
        content={showErrorInfo}
      >
        <NewRegionSelect
          locale={lang}
          radioOptions={[]}
          value={selectValue}
          options={realChannelOptions}
          language={channelLocale}
          button={buttonElementMemo}
          // onChange={(value: any) => channelChange(value)}
          closeOnScroll={true}
          boxClassName="dbo-monitor-dropdown-wrapper"
          onCloseReginSelect={(value: any) => {
            try {
              const { obj, isAll } = getChannelData();
              addFilterBuriedPoint(pointKey, isAll ? 'All' : obj);
            } catch (error) { }
            channelChange(value);
          }}
        ></NewRegionSelect>
      </Bubble>
    </div>
  );
}
